import { askConfirm, askPrompt } from '@/components/ui/ConfirmHost';
import { useBase } from '@/lib/useBase';
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCbtExamsById, postCbtExamsQuestions, putCbtExams, postCbtExamsPush,
  getCbtExamsLive, getCbtExamsResults, getCbtAttempt,
  postCbtAttemptsGrade, postCbtAttemptsVoid, postCbtExamsRelease,
  getQuestionBanks, getQuestionBanksQuestions,
} from '@/api/teacher';
import {
  getClasses,
} from '@/api/owner';
import {
  ArrowLeft, AlertCircle, Plus, Trash2, Send, Radio, BarChart3, HelpCircle,
  Users, Check, X, Clock, RefreshCw, Lock, Unlock, Shield, Save,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;

type Tab = 'overview' | 'questions' | 'push' | 'live' | 'results';

export function OwnerCbtExamDetailPage() {
  const base = useBase();
  const { id } = useParams<{ id: string }>();
  const examId = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');

  const examQ = useQuery({
    queryKey: ['cbt-exam', examId],
    queryFn: () => getCbtExamsById(examId).then(unwrap),
    enabled: !!examId,
    refetchInterval: tab === 'live' ? 5000 : false,
  });

  const exam = examQ.data;

  if (examQ.isLoading) {
    return <div className="p-8 text-center text-slate-500">Loading exam…</div>;
  }
  if (examQ.isError || !exam) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 inline mr-2" />
          Could not load exam. {(examQ.error as any)?.response?.data?.message ?? ''}
        </div>
        <Link to={base + "/cbt-exams"} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-700 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to exams
        </Link>
      </div>
    );
  }

  const isLive = ['live', 'closed'].includes(exam.status);
  const isFinished = ['closed', 'released'].includes(exam.status);
  const canEditQuestions = ['draft', 'scheduled'].includes(exam.status) && (exam.attempts ?? 0) === 0;

  const tabs: { key: Tab; label: string; icon: any; disabled?: boolean }[] = [
    { key: 'overview', label: 'Overview', icon: HelpCircle },
    { key: 'questions', label: `Questions (${exam.questions ?? 0})`, icon: Plus },
    { key: 'push', label: 'Push', icon: Send },
    { key: 'live', label: 'Live', icon: Radio, disabled: !isLive },
    { key: 'results', label: 'Results', icon: BarChart3, disabled: !isFinished && !isLive },
  ];

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <Link to={base + "/cbt-exams"} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> All CBT exams
      </Link>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
            CBT Exam · {exam.kind}
          </p>
          <h1 className="text-3xl font-extrabold text-slate-900">{exam.title}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-slate-600">
            {statusBadge(exam.status)}
            <span>{exam.questions ?? 0} questions</span>
            <span>{exam.attempts ?? 0} attempts</span>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b border-slate-200">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => !t.disabled && setTab(t.key)}
                disabled={t.disabled}
                className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 inline-flex items-center gap-2 transition ${
                  tab === t.key
                    ? 'border-[#0f172a] text-slate-900'
                    : t.disabled
                    ? 'border-transparent text-slate-300 cursor-not-allowed'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'overview' && <OverviewTab exam={exam} onSaved={() => qc.invalidateQueries({ queryKey: ['cbt-exam', examId] })} />}
      {tab === 'questions' && (
        <QuestionsTab
          exam={exam}
          canEdit={canEditQuestions}
          onChanged={() => qc.invalidateQueries({ queryKey: ['cbt-exam', examId] })}
        />
      )}
      {tab === 'push' && <PushTab exam={exam} onPushed={() => qc.invalidateQueries({ queryKey: ['cbt-exam', examId] })} />}
      {tab === 'live' && <LiveTab examId={examId} />}
      {tab === 'results' && <ResultsTab exam={exam} examId={examId} onChanged={() => qc.invalidateQueries({ queryKey: ['cbt-exam', examId] })} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════
function OverviewTab({ exam, onSaved }: { exam: any; onSaved: () => void }) {
  const [instructions, setInstructions] = useState(exam.instructions ?? '');
  const [closesAt, setClosesAt] = useState(exam.closes_at ? exam.closes_at.slice(0, 16) : '');
  const [showResult, setShowResult] = useState(exam.show_result ?? 'after_submit');
  const [error, setError] = useState<string | null>(null);

  const saveM = useMutation({
    mutationFn: () => putCbtExams(exam.id, {
      instructions: instructions.trim() || null,
      closes_at: closesAt || null,
      show_result: showResult,
    }),
    onSuccess: () => { onSaved(); alert('Exam updated.'); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Save failed'),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <h2 className="font-bold text-slate-900">Editable fields</h2>
          <p className="text-xs text-slate-500">
            Title, subject, kind, and duration are locked after creation.
            {(exam.attempts ?? 0) > 0 && ' Only instructions and timing can change once attempts exist.'}
          </p>
          {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">{error}</div>}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Instructions</label>
            <textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Closes at</label>
              <input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Show result</label>
              <select value={showResult} onChange={(e) => setShowResult(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="never">Never</option>
                <option value="after_submit">After submit</option>
                <option value="after_release">After release</option>
              </select>
            </div>
          </div>
          <button onClick={() => saveM.mutate()} disabled={saveM.isPending}
            className="px-4 py-2 bg-[#0f172a] text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1">
            <Save className="w-4 h-4" /> {saveM.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <StatCard label="Duration" value={`${exam.duration_minutes} min`} />
        <StatCard label="Total marks" value={exam.total_marks ?? 0} />
        <StatCard label="Pass mark" value={exam.pass_mark != null ? `${exam.pass_mark}%` : '—'} />
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <p className="text-xs font-semibold text-slate-500 mb-2">Classes</p>
          <div className="flex flex-wrap gap-1">
            {arr<any>(exam.class_ids).length === 0 ? (
              <span className="text-sm text-slate-400 italic">None</span>
            ) : (
              arr<any>(exam.class_ids).map((cid: number, i: number) => (
                <span key={i} className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono">#{cid}</span>
              ))
            )}
          </div>
        </div>
        {exam.opens_at && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-semibold text-slate-500 mb-1">Opens</p>
            <p className="text-sm text-slate-900">{fmtDate(exam.opens_at)}</p>
            {exam.closes_at && (
              <>
                <p className="text-xs font-semibold text-slate-500 mb-1 mt-2">Closes</p>
                <p className="text-sm text-slate-900">{fmtDate(exam.closes_at)}</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: any }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <p className="text-xs font-semibold text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-extrabold text-slate-900">{value}</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  QUESTIONS TAB
// ═══════════════════════════════════════════════════════════════
function QuestionsTab({ exam, canEdit, onChanged }: {
  exam: any; canEdit: boolean; onChanged: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentIds: number[] = arr<any>(exam.question_list).map((q: any) => q.id);

  const removeM = useMutation({
    mutationFn: (qid: number) => {
      const remaining = arr<any>(exam.question_list).filter((q: any) => q.id !== qid);
      return postCbtExamsQuestions(exam.id, {
        questions: remaining.map((q: any) => ({ question_id: q.id, marks: q.marks })),
      });
    },
    onSuccess: () => { onChanged(); alert('Question removed.'); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Remove failed'),
  });

  return (
    <div className="space-y-4">
      {!canEdit && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-start gap-2">
          <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          Questions are locked once the exam is live or has attempts.
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">{error}</div>}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <p className="font-bold text-slate-900">
            {arr<any>(exam.question_list).length} questions · total {exam.total_marks} marks
          </p>
          {canEdit && (
            <button onClick={() => setShowAdd(true)}
              className="text-xs font-bold bg-[#0f172a] text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Add questions
            </button>
          )}
        </div>

        {arr<any>(exam.question_list).length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            No questions yet. {canEdit ? 'Click "Add questions" to include some.' : ''}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {arr<any>(exam.question_list).map((q: any, idx: number) => (
              <li key={q.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 whitespace-pre-wrap">{q.body}</p>
                    <div className="flex gap-2 mt-1 text-xs text-slate-500">
                      <span className="uppercase font-bold">{q.type?.replace('_', ' ')}</span>
                      <span>{q.marks ?? 1} mark(s)</span>
                    </div>
                    {Array.isArray(q.options) && q.options.length > 0 && (
                      <ul className="mt-2 space-y-0.5 text-xs">
                        {q.options.map((o: any) => (
                          <li key={o.id} className={o.is_correct ? 'text-green-700 font-semibold' : 'text-slate-600'}>
                            <span className="font-mono mr-2">{o.label}</span>{o.body}
                            {o.is_correct && <Check className="w-3 h-3 inline ml-1" />}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {canEdit && (
                    <button onClick={async () => { if (await askConfirm('Remove this question from the exam?')) removeM.mutate(q.id); }}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAdd && (
        <AddQuestionsModal
          examId={exam.id}
          currentIds={currentIds}
          currentMarks={arr<any>(exam.question_list).map((q: any) => ({ id: q.id, marks: q.marks }))}
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); onChanged(); }}
        />
      )}
    </div>
  );
}

function AddQuestionsModal({ examId, currentIds, currentMarks, onClose, onAdded }: {
  examId: number;
  currentIds: number[];
  currentMarks: { id: number; marks: any }[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [mode, setMode] = useState<'specific' | 'random'>('specific');
  const [selectedBankId, setSelectedBankId] = useState<number | ''>('');
  const [picks, setPicks] = useState<number[]>([]);
  const [randomBankId, setRandomBankId] = useState<number | ''>('');
  const [randomCount, setRandomCount] = useState('10');
  const [randomDifficulty, setRandomDifficulty] = useState('');
  const [randomType, setRandomType] = useState('');
  const [error, setError] = useState<string | null>(null);

  const banksQ = useQuery({ queryKey: ['question-banks'], queryFn: () => getQuestionBanks().then(unwrap) });
  const bankQuestionsQ = useQuery({
    queryKey: ['bank-questions-pick', selectedBankId],
    queryFn: () => selectedBankId ? getQuestionBanksQuestions(selectedBankId).then(unwrap) : Promise.resolve([]),
    enabled: !!selectedBankId,
  });

  const banks = arr<any>(banksQ.data);
  const bankQuestions = arr<any>(bankQuestionsQ.data).filter((q: any) => !currentIds.includes(q.id));

  const addM = useMutation({
    mutationFn: () => {
      if (mode === 'specific') {
        const merged = [
          ...currentMarks.map((m: any) => ({ question_id: m.id, marks: m.marks })),
          ...picks.map(id => ({ question_id: id })),
        ];
        return postCbtExamsQuestions(examId, { questions: merged });
      }
      const payload: any = {
        random: {
          bank_id: Number(randomBankId),
          count: Number(randomCount),
        },
      };
      if (randomDifficulty) payload.random.difficulty = randomDifficulty;
      if (randomType) payload.random.type = randomType;
      return postCbtExamsQuestions(examId, payload);
    },
    onSuccess: () => onAdded(),
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Add failed'),
  });

  const valid = mode === 'specific' ? picks.length > 0 : !!randomBankId && Number(randomCount) > 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <h3 className="font-bold text-slate-900">Add questions</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 space-y-4">
            {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">{error}</div>}
            <div className="inline-flex bg-slate-100 rounded-lg p-1">
              <button onClick={() => setMode('specific')}
                className={`px-3 py-1.5 text-xs font-bold rounded ${mode === 'specific' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
                Pick specific
              </button>
              <button onClick={() => setMode('random')}
                className={`px-3 py-1.5 text-xs font-bold rounded ${mode === 'random' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
                Random from bank
              </button>
            </div>

            {mode === 'specific' && (
              <div className="space-y-3">
                <select value={selectedBankId} onChange={(e) => { setSelectedBankId(e.target.value ? Number(e.target.value) : ''); setPicks([]); }}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">Choose a bank…</option>
                  {banks.map((b: any) => <option key={b.id} value={b.id}>{b.name} ({b.questions ?? 0})</option>)}
                </select>
                {bankQuestions.length > 0 && (
                  <div className="border border-slate-200 rounded-lg max-h-[300px] overflow-y-auto divide-y divide-slate-100">
                    {bankQuestions.map((q: any) => {
                      const checked = picks.includes(q.id);
                      return (
                        <label key={q.id} className="flex items-start gap-2 p-2 hover:bg-slate-50 cursor-pointer">
                          <input type="checkbox" checked={checked} className="mt-0.5"
                            onChange={() => setPicks(checked ? picks.filter(x => x !== q.id) : [...picks, q.id])} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-slate-900 line-clamp-2">{q.body}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{q.type} · {q.marks ?? 1} mark(s)</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-slate-500">{picks.length} question(s) picked</p>
              </div>
            )}

            {mode === 'random' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Bank *</label>
                  <select value={randomBankId} onChange={(e) => setRandomBankId(e.target.value ? Number(e.target.value) : '')}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Choose a bank…</option>
                    {banks.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">How many *</label>
                  <input type="number" min={1} max={100} value={randomCount}
                    onChange={(e) => setRandomCount(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Difficulty</label>
                  <select value={randomDifficulty} onChange={(e) => setRandomDifficulty(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Any</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Type</label>
                  <select value={randomType} onChange={(e) => setRandomType(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">Any</option>
                    <option value="mcq">MCQ</option>
                    <option value="multi">Multi</option>
                    <option value="true_false">True/False</option>
                    <option value="short">Short</option>
                    <option value="essay">Essay</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
            <button onClick={() => addM.mutate()} disabled={!valid || addM.isPending}
              className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
              {addM.isPending ? 'Adding…' : 'Add to exam'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  PUSH TAB
// ═══════════════════════════════════════════════════════════════
function PushTab({ exam, onPushed }: { exam: any; onPushed: () => void }) {
  const [target, setTarget] = useState<'class' | 'students' | 'devices'>('class');
  const [requireCode, setRequireCode] = useState(false);
  const [studentIds, setStudentIds] = useState('');
  const [deviceIds, setDeviceIds] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastPush, setLastPush] = useState<any>(null);

  const pushM = useMutation({
    mutationFn: () => {
      const body: any = { target, require_code: requireCode };
      if (target === 'students') body.target_ids = studentIds.split(',').map(s => Number(s.trim())).filter(Boolean);
      if (target === 'devices') body.target_ids = deviceIds.split(',').map(s => s.trim()).filter(Boolean);
      return postCbtExamsPush(exam.id, body);
    },
    onSuccess: (r: any) => {
      const d = unwrap(r);
      setLastPush(d);
      onPushed();
      alert(`Pushed! ${d?.students ?? 0} student(s), ${d?.notified ?? 0} notification(s) sent.${d?.access_code ? ` Access code: ${d.access_code}` : ''}`);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Push failed'),
  });

  const pastPushes = arr<any>(exam.pushes);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
        <h2 className="font-bold text-slate-900 flex items-center gap-2">
          <Send className="w-4 h-4" /> Push exam to students
        </h2>
        {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">{error}</div>}

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Target</label>
          <div className="space-y-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={target === 'class'} onChange={() => setTarget('class')} />
              All students in the exam's classes
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={target === 'students'} onChange={() => setTarget('students')} />
              Specific students
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" checked={target === 'devices'} onChange={() => setTarget('devices')} />
              Specific devices
            </label>
          </div>
        </div>

        {target === 'students' && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Student IDs (comma separated)</label>
            <input value={studentIds} onChange={(e) => setStudentIds(e.target.value)}
              placeholder="e.g. 12, 34, 56"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
          </div>
        )}

        {target === 'devices' && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Device IDs (comma separated)</label>
            <input value={deviceIds} onChange={(e) => setDeviceIds(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
          </div>
        )}

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={requireCode} onChange={(e) => setRequireCode(e.target.checked)} />
          Require access code (6-digit, generated and shown to you)
        </label>

        <button onClick={() => pushM.mutate()} disabled={pushM.isPending}
          className="w-full py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Send className="w-4 h-4" /> {pushM.isPending ? 'Pushing…' : 'Push now'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-bold text-slate-900 mb-3">Past pushes</h2>
        {pastPushes.length === 0 ? (
          <p className="text-sm text-slate-500">No pushes yet.</p>
        ) : (
          <ul className="space-y-2">
            {pastPushes.map((p: any) => (
              <li key={p.id} className="p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-900 capitalize">{p.target}</span>
                  <span className="text-xs text-slate-500">{fmtDate(p.pushed_at)}</span>
                </div>
                {p.access_code && (
                  <p className="text-xs mt-1">
                    Code: <span className="font-mono bg-white border border-slate-200 px-1.5 py-0.5 rounded">{p.access_code}</span>
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  LIVE TAB
// ═══════════════════════════════════════════════════════════════
function LiveTab({ examId }: { examId: number }) {
  const liveQ = useQuery({
    queryKey: ['cbt-live', examId],
    queryFn: () => getCbtExamsLive(examId).then(unwrap),
    refetchInterval: 5000,
  });
  const data = liveQ.data;
  const attempts = arr<any>(data?.attempts);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <RefreshCw className="w-3 h-3 animate-spin" /> Auto-refreshing every 5 seconds
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Eligible" value={data?.eligible ?? 0} />
        <StatCard label="Not started" value={data?.counts?.not_started ?? 0} />
        <StatCard label="In progress" value={data?.counts?.in_progress ?? 0} />
        <StatCard label="Finished" value={data?.counts?.finished ?? 0} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">Student</th>
              <th className="px-4 py-3 text-left">Adm. No.</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Answered</th>
              <th className="px-4 py-3 text-right">Time left</th>
              <th className="px-4 py-3 text-left">Flagged</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {attempts.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">No attempts yet.</td></tr>
            ) : (
              attempts.map((a: any) => (
                <tr key={a.attempt_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{a.admission_no}</td>
                  <td className="px-4 py-3">{statusBadge(a.status)}</td>
                  <td className="px-4 py-3 text-right font-mono">{a.answered ?? 0}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {a.time_left_seconds != null
                      ? `${Math.floor(a.time_left_seconds / 60)}:${String(a.time_left_seconds % 60).padStart(2, '0')}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {a.flagged && <span className="text-red-600 text-xs font-bold">⚠ Flagged</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
//  RESULTS TAB
// ═══════════════════════════════════════════════════════════════
function ResultsTab({ exam, examId, onChanged }: { exam: any; examId: number; onChanged: () => void }) {
  const [openAttempt, setOpenAttempt] = useState<number | null>(null);

  const resultsQ = useQuery({
    queryKey: ['cbt-results', examId],
    queryFn: () => getCbtExamsResults(examId).then(unwrap),
  });
  const data = resultsQ.data;
  const attempts = arr<any>(data?.attempts);

  const releaseM = useMutation({
    mutationFn: (force: boolean) => postCbtExamsRelease(examId, { force }),
    onSuccess: (r: any) => {
      const d = unwrap(r);
      alert(`Released. ${d?.pushed_to_results ?? 0} score(s) pushed to result column.`);
      onChanged();
      resultsQ.refetch();
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message ?? 'Release failed';
      if (msg.toLowerCase().includes('still writing') || msg.toLowerCase().includes('essay')) {
        void askConfirm(msg + '\n\nForce release anyway?').then((ok) => { if (ok) releaseM.mutate(true); });
      } else {
        alert(msg);
      }
    },
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Students" value={data?.stats?.students ?? 0} />
        <StatCard label="Average" value={`${data?.stats?.average ?? 0}%`} />
        <StatCard label="Highest" value={data?.stats?.highest ?? 0} />
        <StatCard label="Lowest" value={data?.stats?.lowest ?? 0} />
      </div>

      <div className="flex justify-end">
        <button onClick={async () => { if (await askConfirm('Release this exam? Scores will be pushed to the result column if configured.')) releaseM.mutate(false); }}
          disabled={releaseM.isPending || exam.status === 'released'}
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
          <Unlock className="w-4 h-4" />
          {exam.status === 'released' ? 'Already released' : releaseM.isPending ? 'Releasing…' : 'Release scores'}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">Student</th>
              <th className="px-4 py-3 text-left">Adm. No.</th>
              <th className="px-4 py-3 text-right">Attempt</th>
              <th className="px-4 py-3 text-right">Score</th>
              <th className="px-4 py-3 text-right">%</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Needs marking</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {attempts.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-slate-500">No results yet.</td></tr>
            ) : (
              attempts.map((a: any) => (
                <tr key={a.attempt_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{a.admission_no}</td>
                  <td className="px-4 py-3 text-right">{a.attempt_no}</td>
                  <td className="px-4 py-3 text-right font-mono">{a.score ?? '—'}</td>
                  <td className="px-4 py-3 text-right font-mono">{a.percent != null ? `${a.percent}%` : '—'}</td>
                  <td className="px-4 py-3">{statusBadge(a.status)}</td>
                  <td className="px-4 py-3 text-right">
                    {a.needs_marking > 0 ? (
                      <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                        {a.needs_marking}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setOpenAttempt(a.attempt_id)}
                      className="text-xs font-semibold text-blue-600 hover:underline">
                      Open
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {openAttempt && (
        <AttemptDetailModal
          attemptId={openAttempt}
          onClose={() => setOpenAttempt(null)}
          onChanged={() => { resultsQ.refetch(); onChanged(); }}
        />
      )}
    </div>
  );
}

function AttemptDetailModal({ attemptId, onClose, onChanged }: {
  attemptId: number; onClose: () => void; onChanged: () => void;
}) {
  const qc = useQueryClient();
  const attemptQ = useQuery({
    queryKey: ['cbt-attempt', attemptId],
    queryFn: () => getCbtAttempt(attemptId).then(unwrap),
  });
  const attempt = attemptQ.data;
  const answers = arr<any>(attempt?.answers);

  const [scores, setScores] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);

  const saveM = useMutation({
    mutationFn: () => {
      const payload = Object.entries(scores)
        .filter(([_, v]) => v !== '' && !isNaN(Number(v)))
        .map(([qid, v]) => ({ question_id: Number(qid), score: Number(v) }));
      return postCbtAttemptsGrade(attemptId, { answers: payload });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cbt-attempt', attemptId] }); onChanged(); alert('Marks saved.'); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Save failed'),
  });

  const voidM = useMutation({
    mutationFn: (reason: string) => postCbtAttemptsVoid(attemptId, { reason }),
    onSuccess: () => { onChanged(); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Void failed'),
  });

  const needsMarking = answers.filter((a: any) => a.needs_marking).length;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <div>
              <h3 className="font-bold text-slate-900">
                {attempt?.student?.name ?? 'Attempt'}
              </h3>
              <p className="text-xs text-slate-500">
                {attempt?.title} · {attempt?.status} · score {attempt?.score ?? '—'} / {attempt?.total_marks ?? '?'}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>

          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {error && <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">{error}</div>}

            {needsMarking > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800">
                {needsMarking} essay/short answer(s) need manual marking below.
              </div>
            )}

            {answers.map((a: any, idx: number) => (
              <div key={a.question_id} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-500">Question {idx + 1} · {a.type} · {a.marks} mark(s)</p>
                    <p className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">{a.body}</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                    a.needs_marking ? 'bg-amber-100 text-amber-800' :
                    a.is_correct ? 'bg-green-100 text-green-800' :
                    a.score != null ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {a.score != null ? `${a.score} / ${a.marks}` : a.needs_marking ? 'Needs marking' : '—'}
                  </span>
                </div>

                <div className="text-sm text-slate-700 bg-slate-50 rounded p-2">
                  <span className="text-xs font-bold text-slate-500">Answer: </span>
                  {a.answer != null ? String(a.answer) : <span className="italic text-slate-400">no answer</span>}
                </div>

                {a.options && a.options.length > 0 && (
                  <ul className="mt-2 space-y-0.5 text-xs">
                    {a.options.map((o: any) => (
                      <li key={o.id} className={o.is_correct ? 'text-green-700 font-semibold' : 'text-slate-500'}>
                        {o.label}. {o.body}
                        {o.is_correct && <Check className="w-3 h-3 inline ml-1" />}
                      </li>
                    ))}
                  </ul>
                )}

                {a.needs_marking && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs font-semibold text-slate-700">Score:</label>
                    <input type="number" min={0} max={a.marks} step={0.5}
                      defaultValue={a.score ?? ''}
                      onChange={(e) => setScores({ ...scores, [a.question_id]: e.target.value })}
                      className="w-20 border border-slate-300 rounded px-2 py-1 text-sm" />
                    <span className="text-xs text-slate-500">/ {a.marks}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={async () => {
              const reason = await askPrompt('The student can try again if attempts remain. The reason is kept with the attempt.', { title: 'Void this attempt?', confirmLabel: 'Void attempt', placeholder: 'e.g. Power went off during the test', destructive: true });
              if (reason) voidM.mutate(reason);
            }}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg font-semibold text-sm hover:bg-red-50">
              Void attempt
            </button>
            <div className="flex-1" />
            <button onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Close</button>
            {needsMarking > 0 && (
              <button onClick={() => saveM.mutate()} disabled={saveM.isPending}
                className="px-4 py-2 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
                {saveM.isPending ? 'Saving…' : 'Save marks'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function statusBadge(status: string) {
  const map: Record<string, { label: string; tone: string }> = {
    draft:          { label: 'Draft',          tone: 'bg-slate-100 text-slate-700 border-slate-200' },
    scheduled:      { label: 'Scheduled',      tone: 'bg-blue-50 text-blue-700 border-blue-200' },
    live:           { label: 'Live',           tone: 'bg-green-50 text-green-700 border-green-200' },
    closed:         { label: 'Closed',         tone: 'bg-amber-50 text-amber-800 border-amber-200' },
    released:       { label: 'Released',       tone: 'bg-slate-50 text-slate-600 border-slate-200' },
    in_progress:    { label: 'In progress',    tone: 'bg-blue-50 text-blue-700 border-blue-200' },
    submitted:      { label: 'Submitted',      tone: 'bg-green-50 text-green-700 border-green-200' },
    auto_submitted: { label: 'Auto-submitted', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
    voided:         { label: 'Voided',         tone: 'bg-red-50 text-red-700 border-red-200' },
  };
  const s = map[status] ?? { label: status, tone: 'bg-slate-100 text-slate-600 border-slate-200' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${s.tone}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${
        status === 'live' || status === 'in_progress' ? 'bg-green-600 animate-pulse' :
        status === 'submitted' ? 'bg-green-600' :
        status === 'scheduled' ? 'bg-blue-600' :
        status === 'voided' ? 'bg-red-600' :
        status === 'closed' || status === 'auto_submitted' ? 'bg-amber-600' : 'bg-slate-400'
      }`} />
      {s.label}
    </span>
  );
}
